/**
 * First-run guidance state for a freshly provisioned workshop (#1853, #1498).
 *
 * Progress lives in `tenants.settings.onboarding` rather than being derived
 * from whether the data exists, because a derived state cannot tell "not done
 * yet" apart from "deliberately skipped" — it would ask again on every sign-in
 * for something the admin already declined.
 *
 * The settings PATCH deep-merges only a fixed list of keys and `onboarding` is
 * not one of them, so every write here sends the COMPLETE object. Sending a
 * fragment would silently drop the other fields.
 *
 * Writes are serialized against the last successful snapshot, not the render
 * that captured the click. `useUpdateTenantSettings` invalidates without
 * awaiting the refetch, so two completions before the cache catches up would
 * otherwise each PATCH from the same stale object and the later write would
 * drop the earlier step.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useTenantSettings, useUpdateTenantSettings } from '@/hooks/useTenantSettings';
import {
  finishOnboardingState,
  isOnboardingSnoozed,
  nextOnboardingState,
  ONBOARDING_STEP_PERMISSION,
  OPTIONAL_ONBOARDING_STEPS,
  readOnboardingState,
  resumedOnboardingState,
  skippedOnboardingState,
  type OnboardingState,
  type OnboardingStep,
} from './onboardingState';

export {
  ONBOARDING_STEPS,
  OPTIONAL_ONBOARDING_STEPS,
  ONBOARDING_STEP_PERMISSION,
  resumeStepIndex,
  isOptionalOnboardingStep,
} from './onboardingState';
export type { OnboardingStep, OnboardingState };

function snapshotKey(state: OnboardingState): string {
  return `${state.completedSteps.join(',')}|${state.completedAt ?? ''}|${state.skippedAt ?? ''}`;
}

/** ISO-8601 timestamps from `toISOString()` sort lexicographically. */
function isOlderIso(
  candidate: string | null | undefined,
  baseline: string | null | undefined,
): boolean {
  return Boolean(candidate && baseline && candidate < baseline);
}

/**
 * Cache may replace a local snapshot only when it still holds everything we
 * persisted. Skip and resume are opposite transitions, so a matching
 * skippedAt polarity is required — a stale refetch that still has skippedAt
 * is not newer than a resume that cleared it. Polarity is not enough after a
 * renewed snooze: yesterday's nonempty timestamp must not undo today's.
 */
function isSameOrNewer(candidate: OnboardingState, baseline: OnboardingState): boolean {
  if (!baseline.completedSteps.every((step) => candidate.completedSteps.includes(step))) {
    return false;
  }
  if (baseline.completedAt && !candidate.completedAt) return false;
  if (isOlderIso(candidate.completedAt, baseline.completedAt)) return false;
  if (Boolean(baseline.skippedAt) !== Boolean(candidate.skippedAt)) return false;
  if (isOlderIso(candidate.skippedAt, baseline.skippedAt)) return false;
  return true;
}

export function useOnboarding() {
  const { tenantSettings, isLoading, isFetched } = useTenantSettings();
  const updateSettings = useUpdateTenantSettings();

  // Saving progress goes through `PATCH /tenants/me/settings`, which is
  // `requireRole('admin')`. Gate on the workshop-settings permission so a
  // mechanic is never prompted to do something the API would refuse.
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission('settings.edit_workshop');

  const cacheState = useMemo(
    () => readOnboardingState(tenantSettings?.onboarding),
    [tenantSettings?.onboarding],
  );

  const savedStateRef = useRef(cacheState);
  const hasWrittenRef = useRef(false);
  const [state, setState] = useState(cacheState);
  const [pendingCount, setPendingCount] = useState(0);
  const chainRef = useRef(Promise.resolve());
  const inflightRef = useRef(new Map<string, Promise<void>>());

  // Query owns the snapshot until a local write succeeds. After that, the
  // last successful write owns it unless the cache is a same-or-newer echo.
  // Adopt during render so the first fetched frame is already hydrated —
  // an effect would leave the wizard to resume from the empty default.
  const cacheOwns =
    pendingCount === 0 &&
    (!hasWrittenRef.current || isSameOrNewer(cacheState, savedStateRef.current));

  if (cacheOwns) {
    savedStateRef.current = cacheState;
  }

  if (cacheOwns && snapshotKey(state) !== snapshotKey(cacheState)) {
    setState(cacheState);
  }

  const displayState = cacheOwns ? cacheState : state;

  const isComplete = Boolean(displayState.completedAt);
  const isSkipped = Boolean(displayState.skippedAt);
  const isSnoozed = isOnboardingSnoozed(displayState.skippedAt);

  const mutateSettings = updateSettings.mutateAsync;

  /** Every write sends the whole object — the server replaces, not merges. */
  const save = useCallback(
    (next: OnboardingState) => mutateSettings({ onboarding: next }),
    [mutateSettings],
  );

  const enqueue = useCallback(
    (transform: (current: OnboardingState) => OnboardingState | null): Promise<void> => {
      setPendingCount((count) => count + 1);
      const run = chainRef.current.then(async () => {
        const next = transform(savedStateRef.current);
        if (!next) return;
        await save(next);
        hasWrittenRef.current = true;
        savedStateRef.current = next;
        setState(next);
      });
      chainRef.current = run.then(
        () => undefined,
        () => undefined,
      );
      return run.finally(() => {
        setPendingCount((count) => count - 1);
      });
    },
    [save],
  );

  const coalesce = useCallback((key: string, start: () => Promise<void>): Promise<void> => {
    const existing = inflightRef.current.get(key);
    if (existing) return existing;
    const promise = start();
    inflightRef.current.set(key, promise);
    void promise
      .finally(() => {
        if (inflightRef.current.get(key) === promise) {
          inflightRef.current.delete(key);
        }
      })
      .catch(() => undefined);
    return promise;
  }, []);

  const completeStep = useCallback(
    (step: OnboardingStep) => {
      if (savedStateRef.current.completedSteps.includes(step)) {
        return Promise.resolve();
      }
      return coalesce(`step:${step}`, () =>
        enqueue((current) => nextOnboardingState(current, step, new Date().toISOString())),
      );
    },
    [coalesce, enqueue],
  );

  /** Dismissing the whole wizard. Recorded as a snooze, not a permanent skip. */
  const skipAll = useCallback(
    () =>
      coalesce('skipAll', () =>
        enqueue((current) => skippedOnboardingState(current, new Date().toISOString())),
      ),
    [coalesce, enqueue],
  );

  /** Reopening after a skip; used by the settings entry point and Continue. */
  const resume = useCallback(
    () => coalesce('resume', () => enqueue((current) => resumedOnboardingState(current))),
    [coalesce, enqueue],
  );

  /** Marks remaining optional steps done in a single PATCH, including skipped staff. */
  const finishRemaining = useCallback(
    () =>
      coalesce('finish', () =>
        enqueue((current) => {
          if (
            current.completedAt &&
            OPTIONAL_ONBOARDING_STEPS.every((step) => current.completedSteps.includes(step))
          ) {
            return null;
          }
          return finishOnboardingState(current, new Date().toISOString());
        }),
      ),
    [coalesce, enqueue],
  );

  const canEditStep = useCallback(
    (step: OnboardingStep) => hasPermission(ONBOARDING_STEP_PERMISSION[step]),
    [hasPermission],
  );

  return {
    state: displayState,
    isLoading,
    isFetched,
    canManage,
    canEditStep,
    isComplete,
    isSkipped,
    isSnoozed,
    /**
     * Whether to nudge the admin. Hidden until settings have actually loaded,
     * so a slow request cannot flash a prompt at a workshop that finished
     * onboarding months ago. A skip snoozes the prompt; completion stops it.
     */
    shouldPrompt: isFetched && canManage && !isComplete && !isSnoozed,
    isSaving: pendingCount > 0,
    completeStep,
    finishRemaining,
    skipAll,
    resume,
  };
}
